/**
 * API Service
 * 
 * Axios-based HTTP client for communicating with the backend API.
 * Automatically attaches authentication tokens to requests.
 */

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { authService } from './authService'
import {
  getApiBaseUrl,
  getFileServiceUrl,
  getAnalysisServiceUrl,
  isLocalAuthBypassEnabled,
} from '@/config/authConfig'
import type {
  AuthUser,
  RegistrationRequest,
  DomainCheckResponse,
  Device,
  DeviceListResponse,
  User,
  UserListResponse,
  UpdateUserRolesRequest,
  DashboardStats,
  SorFileDto,
  SorFileListResponse,
  SasUrlResponse,
  DeleteFileResponse,
  FileListParams,
  ContainerSasResponse,
  UploadSasRequest,
  TraceUploadResponse,
  AlignmentResponse,
  BiDiAnalysisResult,
  FiberAnalysisResult,
  SpliceLossResult,
  EventDetectionResult,
  ReportResult,
  AnalysisParameters,
  PendingClaimsResponse,
  ClaimModeResponse,
  CustomerListResponse,
  SuspendActivateResponse,
  CaStatusResponse,
  DeviceCertificateListResponse,
  PendingDomain,
  PendingUser,
} from '@/types'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

async function attachAuthHeader(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  if (isLocalAuthBypassEnabled()) {
    return config
  }

  try {
    const token = await authService.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    console.warn('[API] Failed to get access token:', error)
  }
  return config
}

// Request interceptor to add auth token
api.interceptors.request.use(
  attachAuthHeader,
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status
      
      switch (status) {
        case 401:
          console.error('[API] Unauthorized - token may be expired')
          // Could trigger re-authentication here
          break
        case 403:
          console.error('[API] Forbidden - insufficient permissions')
          break
        case 404:
          console.error('[API] Not found')
          break
        case 500:
          console.error('[API] Server error')
          break
      }
    } else if (error.request) {
      console.error('[API] Network error - no response received')
    } else {
      console.error('[API] Request setup error:', error.message)
    }
    
    return Promise.reject(error)
  }
)

// ============================================================================
// Authentication API
// ============================================================================

export const authApi = {
  /**
   * Get current user info from backend (includes Graph API enrichment)
   */
  async getMe(): Promise<AuthUser> {
    const response = await api.get<AuthUser>('/api/v1/auth/me')
    return response.data
  },

  /**
   * Complete user registration (first-time setup)
   */
  async completeRegistration(request: RegistrationRequest): Promise<AuthUser> {
    const response = await api.post<AuthUser>('/api/v1/auth/complete-registration', request)
    return response.data
  },

  /**
   * Check if email domain is allowed for sign-up
   */
  async checkDomain(domain: string): Promise<DomainCheckResponse> {
    const response = await api.get<DomainCheckResponse>('/api/v1/auth/check-domain', {
      params: { domain },
    })
    return response.data
  },
}

// ============================================================================
// Device API
// ============================================================================

export const deviceApi = {
  /**
   * Get devices for the current customer (DeviceUser, DeviceAdmin)
   * This is the primary endpoint for most users.
   */
  async getMyDevices(pageSize = 100, claimedOnly = true): Promise<DeviceListResponse> {
    const response = await api.get<DeviceListResponse>('/api/v1/devices/my-devices', {
      params: { pageSize, claimedOnly },
    })
    return response.data
  },

  /**
   * Get all devices across all customers (SuperAdmin only)
   */
  async getAllDevices(pageSize = 100, claimedOnly?: boolean, customerId?: string): Promise<DeviceListResponse> {
    const response = await api.get<DeviceListResponse>('/api/v1/devices', {
      params: { pageSize, claimedOnly, customerId },
    })
    return response.data
  },

  /**
   * Get single device by ID
   */
  async getDevice(deviceId: string): Promise<Device> {
    const response = await api.get<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}`)
    return response.data
  },

  /**
   * Get unclaimed devices available to claim
   * Uses my-devices with claimedOnly=false to get unclaimed devices visible to this customer
   */
  async getUnclaimedDevices(pageSize = 100): Promise<DeviceListResponse> {
    const response = await api.get<DeviceListResponse>('/api/v1/devices/my-devices', {
      params: { pageSize, claimedOnly: false },
    })
    return response.data
  },

  /**
   * Claim a device for the current customer
   */
  async claimDevice(deviceId: string): Promise<Device> {
    const response = await api.patch<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}/claim`)
    return response.data
  },

  /**
   * Unclaim a device (release back to unclaimed pool)
   */
  async unclaimDevice(deviceId: string): Promise<Device> {
    const response = await api.delete<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}/claim`)
    return response.data
  },

  /**
   * Update device metadata (e.g., friendly name).
   * Sends an UpdateDeviceRequest to the backend PATCH endpoint.
   */
  async updateDevice(deviceId: string, request: { friendlyName: string }): Promise<Device> {
    const response = await api.patch<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}`, request)
    return response.data
  },

  /**
   * Get pending and denied claims for the current customer.
   * SuperAdmins see all pending claims across all customers, or can filter by customerId.
   */
  async getPendingClaims(customerId?: string): Promise<PendingClaimsResponse> {
    const response = await api.get<PendingClaimsResponse>('/api/v1/devices/pending-claims', {
      params: { ...(customerId && { customerId }) },
    })
    return response.data
  },

  /**
   * Manually check the status of a pending claim.
   * Triggers an immediate check of the device's reported properties.
   */
  async checkClaimStatus(deviceId: string): Promise<Device> {
    const response = await api.post<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}/claim/check`)
    return response.data
  },

  /**
   * Cancel a pending or denied claim.
   * Returns the device to unclaimed state.
   */
  async cancelPendingClaim(deviceId: string, reason?: string): Promise<Device> {
    const response = await api.delete<Device>(`/api/v1/devices/${encodeURIComponent(deviceId)}/claim/pending`, {
      data: reason ? { reason } : undefined,
    })
    return response.data
  },

  /**
   * Get the current claim mode configuration.
   * Returns whether the system uses sync or async claiming.
   */
  async getClaimMode(): Promise<ClaimModeResponse> {
    const response = await api.get<ClaimModeResponse>('/api/v1/devices/claim-mode')
    return response.data
  },

  /**
   * Override a device claim to a specific customer (SuperAdmin only)
   */
  async overrideClaim(deviceId: string, customerId: string): Promise<any> {
    const response = await api.post(`/api/v1/devices/${deviceId}/claim/override`, { customerId })
    return response.data
  },

  /**
   * Quick-claim a device directly to a customer (SuperAdmin only)
   */
  async quickClaim(deviceId: string, customerId: string): Promise<any> {
    const response = await api.post(`/api/v1/devices/${deviceId}/claim/quick`, { customerId })
    return response.data
  },

  /**
   * Approve a pending claim on behalf of a customer (SuperAdmin only)
   */
  async approveClaimOnBehalf(deviceId: string): Promise<void> {
    await api.post(`/api/v1/devices/${deviceId}/claim/approve`)
  },

  /**
   * Deny a pending claim on behalf of a customer (SuperAdmin only)
   */
  async denyClaimOnBehalf(deviceId: string): Promise<void> {
    await api.post(`/api/v1/devices/${deviceId}/claim/deny`)
  },

  /**
   * Disable a device in IoT Hub (SuperAdmin only)
   */
  async disableDevice(deviceId: string): Promise<void> {
    await api.post(`/api/v1/devices/${deviceId}/disable`)
  },

  /**
   * Enable a device in IoT Hub (SuperAdmin only)
   */
  async enableDevice(deviceId: string): Promise<void> {
    await api.post(`/api/v1/devices/${deviceId}/enable`)
  },
}

// ============================================================================
// User API
// ============================================================================

export const userApi = {
  /**
   * Get all users for current customer (Admin only).
   * SuperAdmins can filter by customerId.
   */
  async getUsers(page = 0, pageSize = 20, customerId?: string): Promise<UserListResponse> {
    const response = await api.get<UserListResponse>('/api/v1/users', {
      params: { page, size: pageSize, ...(customerId && { customerId }) },
    })
    return response.data
  },

  /**
   * Get single user by ID
   */
  async getUser(userId: string): Promise<User> {
    const response = await api.get<User>(`/api/v1/users/${encodeURIComponent(userId)}`)
    return response.data
  },

  /**
   * Update user roles (Admin only)
   */
  async updateUserRoles(userId: string, request: UpdateUserRolesRequest): Promise<User> {
    const response = await api.put<User>(`/api/v1/users/${encodeURIComponent(userId)}/roles`, request)
    return response.data
  },

  /**
   * Delete user (Admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/api/v1/users/${encodeURIComponent(userId)}`)
  },

  /**
   * Enable/disable user (Admin only)
   */
  async setUserEnabled(userId: string, enabled: boolean): Promise<User> {
    const response = await api.patch<User>(`/api/v1/users/${encodeURIComponent(userId)}`, {
      enabled,
    })
    return response.data
  },

  async getPendingRegistrations(customerId?: string): Promise<PendingUser[]> {
    const response = await api.get<PendingUser[]>('/api/v1/users/pending', {
      params: customerId ? { customerId } : {},
    })
    return response.data
  },

  async approveRegistration(userId: string): Promise<void> {
    await api.post(`/api/v1/users/${userId}/approve-registration`)
  },

  async rejectRegistration(userId: string): Promise<void> {
    await api.post(`/api/v1/users/${userId}/reject-registration`)
  },
}

// ============================================================================
// Dashboard API
// ============================================================================

export const dashboardApi = {
  /**
   * Get dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/api/v1/dashboard/stats')
    return response.data
  },
}

// ============================================================================
// File Service API (separate backend)
// ============================================================================

// Create separate axios instance for file service
const fileApi: AxiosInstance = axios.create({
  baseURL: getFileServiceUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token (same as main api)
fileApi.interceptors.request.use(
  attachAuthHeader,
  (error) => Promise.reject(error)
)

// Response interceptor for error handling (same as main api)
fileApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status
      switch (status) {
        case 401:
          console.error('[FileAPI] Unauthorized - token may be expired')
          break
        case 403:
          console.error('[FileAPI] Forbidden - insufficient permissions')
          break
        case 404:
          console.error('[FileAPI] Not found')
          break
        case 500:
          console.error('[FileAPI] Server error')
          break
      }
    } else if (error.request) {
      console.error('[FileAPI] Network error - no response received')
    } else {
      console.error('[FileAPI] Request setup error:', error.message)
    }
    return Promise.reject(error)
  }
)

export const sorFileApi = {
  /**
   * Get container SAS URL for direct blob access.
   * Returns a container-level SAS with role-based permissions:
   * - FileUser: read, list, filter by tags (rlf)
   * - FileAdmin/SuperAdmin: read, list, filter, delete (rlfd)
   */
  async getContainerSas(ttlMinutes = 15): Promise<ContainerSasResponse> {
    const response = await fileApi.get<ContainerSasResponse>(
      '/api/v1/files/container-sas',
      { params: { ttlMinutes } }
    )
    return response.data
  },

  /**
   * List files with optional filtering
   * @deprecated Use blobStorageService with getContainerSas() instead
   */
  async listFiles(params: FileListParams = {}): Promise<SorFileListResponse> {
    const response = await fileApi.get<SorFileListResponse>('/api/v1/files', {
      params: {
        customerId: params.customerId,
        prefix: params.prefix,
        deviceId: params.deviceId,
        pageSize: params.pageSize ?? 50,
        continuationToken: params.continuationToken,
      },
    })
    return response.data
  },

  /**
   * List files by device ID
   */
  async listFilesByDevice(deviceId: string, pageSize = 50): Promise<SorFileListResponse> {
    const response = await fileApi.get<SorFileListResponse>(
      `/api/v1/files/by-device/${encodeURIComponent(deviceId)}`,
      { params: { pageSize } }
    )
    return response.data
  },

  /**
   * Get file metadata
   */
  async getFileMetadata(blobName: string): Promise<SorFileDto> {
    const response = await fileApi.get<SorFileDto>(
      `/api/v1/files/${encodeURIComponent(blobName)}/metadata`
    )
    return response.data
  },

  /**
   * Get download SAS URL for a file
   */
  async getDownloadUrl(blobName: string, ttlMinutes = 15): Promise<SasUrlResponse> {
    const response = await fileApi.get<SasUrlResponse>(
      `/api/v1/files/download`,
      { params: { blobName, ttlMinutes } }
    )
    return response.data
  },

  /**
   * Delete file (FileAdmin/SuperAdmin only)
   */
  async deleteFile(blobName: string): Promise<DeleteFileResponse> {
    const response = await fileApi.delete<DeleteFileResponse>(
      `/api/v1/files/${encodeURIComponent(blobName)}`
    )
    return response.data
  },

  /**
   * Get upload SAS URL for uploading a new file to the incoming container.
   * Files uploaded here will be processed by IncomingPoller and moved to
   * the customer container with proper blob tags for filtering.
   *
   * Only FileAdmin and SuperAdmin can upload files.
   *
   * Required metadata fields:
   * - device_id: The device this file belongs to
   * - test_id: Test identifier
   * - location_a: Start location
   * - location_b: End location
   * - last_modified_on_device: Original file modification timestamp
   *
   * @param request Upload request with blob name and required metadata
   * @returns SAS URL for uploading the file to incoming container
   */
  async getUploadUrl(request: UploadSasRequest): Promise<SasUrlResponse> {
    const response = await fileApi.post<SasUrlResponse>(
      '/api/v1/files/incoming-upload-url',
      request
    )
    return response.data
  },
}

// ============================================================================
// Analysis Service API (separate backend)
// ============================================================================

// Create separate axios instance for analysis service
const analysisApi: AxiosInstance = axios.create({
  baseURL: getAnalysisServiceUrl(),
  timeout: 60000, // Longer timeout for file uploads and analysis
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
analysisApi.interceptors.request.use(
  attachAuthHeader,
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
analysisApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status
      switch (status) {
        case 401:
          console.error('[AnalysisAPI] Unauthorized - token may be expired')
          break
        case 403:
          console.error('[AnalysisAPI] Forbidden - insufficient permissions')
          break
        case 404:
          console.error('[AnalysisAPI] Not found')
          break
        case 500:
          console.error('[AnalysisAPI] Server error')
          break
      }
    } else if (error.request) {
      console.error('[AnalysisAPI] Network error - no response received')
    } else {
      console.error('[AnalysisAPI] Request setup error:', error.message)
    }
    return Promise.reject(error)
  }
)

/**
 * Trace API - Upload and manage SOR trace files
 */
export const traceApi = {
  /**
   * Upload a SOR trace file
   */
  async uploadTrace(
    file: File,
    traceType: 'SOP' | 'EOP',
    sessionId?: string
  ): Promise<{ sessionId: string; trace: TraceUploadResponse }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('traceType', traceType)
    if (sessionId) {
      formData.append('sessionId', sessionId)
    }
    const response = await analysisApi.post<{ sessionId: string; trace: TraceUploadResponse }>(
      '/api/traces/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  /**
   * Align SOP and EOP traces
   */
  async alignTraces(sessionId: string, linkLength: number): Promise<AlignmentResponse> {
    const response = await analysisApi.post<AlignmentResponse>('/api/traces/align', {
      sessionId,
      linkLength,
    })
    return response.data
  },

  /**
   * Apply manual alignment adjustment
   */
  async manualAlign(sessionId: string, samples: number): Promise<AlignmentResponse> {
    const response = await analysisApi.post<AlignmentResponse>(
      `/api/traces/manual-align/${encodeURIComponent(sessionId)}`,
      { samples }
    )
    return response.data
  },

  /**
   * Delete an analysis session
   */
  async deleteSession(sessionId: string): Promise<void> {
    await analysisApi.delete(`/api/traces/session/${encodeURIComponent(sessionId)}`)
  },
}

/**
 * Trace Comparison API - upload and list multiple traces in one session
 */
export const traceComparisonApi = {
  /**
   * Upload one trace for Trace Comparison page
   */
  async uploadTrace(
    file: File,
    sessionId?: string
  ): Promise<{ sessionId: string; trace: TraceUploadResponse }> {
    const formData = new FormData()
    formData.append('file', file)
    if (sessionId) {
      formData.append('sessionId', sessionId)
    }
    const response = await analysisApi.post<{ sessionId: string; trace: TraceUploadResponse }>(
      '/api/trace-comparison/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  /**
   * List all uploaded comparison traces for a session
   */
  async listSessionTraces(sessionId: string): Promise<{ sessionId: string; traces: TraceUploadResponse[] }> {
    const response = await analysisApi.get<{ sessionId: string; traces: TraceUploadResponse[] }>(
      `/api/trace-comparison/session/${encodeURIComponent(sessionId)}/traces`
    )
    return response.data
  },

  /**
   * Delete one comparison trace by trace ID
   */
  async deleteTrace(sessionId: string, traceId: string): Promise<{ sessionId: string; traceId: string; deleted: boolean }> {
    const response = await analysisApi.delete<{ sessionId: string; traceId: string; deleted: boolean }>(
      `/api/trace-comparison/session/${encodeURIComponent(sessionId)}/traces/${encodeURIComponent(traceId)}`
    )
    return response.data
  },

  /**
   * Clear all comparison traces in a session
   */
  async clearAllTraces(sessionId: string): Promise<{ sessionId: string; deletedCount: number }> {
    const response = await analysisApi.delete<{ sessionId: string; deletedCount: number }>(
      `/api/trace-comparison/session/${encodeURIComponent(sessionId)}/traces`
    )
    return response.data
  },

  /**
   * Compute attenuation using exactly two selected traces
   */
  async computeAttenuation(request: {
    sessionId: string
    traceOne: string
    traceTwo: string
    offsets?: Record<string, { dbOffset: number; xOffset: number }>
  }): Promise<{ sessionId: string; traceOne: string; traceTwo: string; attenuationTrace: TraceUploadResponse }> {
    const response = await analysisApi.post<{ sessionId: string; traceOne: string; traceTwo: string; attenuationTrace: TraceUploadResponse }>(
      '/api/trace-comparison/compute-attenuation',
      request
    )
    return response.data
  },
}

/**
 * BiDi Analysis API - Calculate attenuation and backscatter
 */
export const biDiApi = {
  /**
   * Process BiDi analysis to get attenuation and backscatter traces
   */
  async processResults(sessionId: string): Promise<BiDiAnalysisResult> {
    const response = await analysisApi.post<BiDiAnalysisResult>('/api/bidi/process-results', {
      sessionId,
    })
    return response.data
  },

  /**
   * Generate analysis report (direct download)
   */
  async generateReport(sessionId: string): Promise<{ blob: Blob; filename: string; contentType: string }> {
    const response = await analysisApi.post('/api/bidi/generate-report', { sessionId }, {
      responseType: 'blob',
    })
    const contentDisposition = response.headers['content-disposition'] as string | undefined
    const contentType = (response.headers['content-type'] as string | undefined)
      ?? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

    // Prefer RFC 5987 filename*=, then fallback to quoted/unquoted filename=
    const now = new Date()
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`
    let filename = `BiDi-Report-${timestamp}.docx`
    if (contentDisposition) {
      const utf8Match = contentDisposition.match(/filename\\*=UTF-8''([^;]+)/i)
      const basicMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i)
      const rawName = utf8Match?.[1] ?? basicMatch?.[1]
      if (rawName) {
        filename = decodeURIComponent(rawName)
      }
    }

    return { blob: response.data as Blob, filename, contentType }
  },

  /**
   * Generate and store analysis report (for report panel)
   */
  async generateReportStored(sessionId: string): Promise<ReportResult> {
    const response = await analysisApi.post<ReportResult>('/api/bidi/generate-report-stored', {
      sessionId,
    })
    return response.data
  },
}

/**
 * Fiber Analysis API - Fiber segmentation and splice analysis
 */
export const fiberApi = {
  /**
   * Find individual fibers in the trace
   */
  async showFibres(
    sessionId: string,
    params?: AnalysisParameters
  ): Promise<FiberAnalysisResult> {
    const response = await analysisApi.post<FiberAnalysisResult>('/api/fiber/show-fibres', {
      sessionId,
      ...params,
    })
    return response.data
  },

  /**
   * Add or remove a splice peak manually
   */
  async updatePeaks(
    sessionId: string,
    action: 'add' | 'remove',
    clickedDistance: number
  ): Promise<FiberAnalysisResult> {
    const response = await analysisApi.post<FiberAnalysisResult>('/api/fiber/update-peaks', {
      sessionId,
      action,
      clickedDistance,
    })
    return response.data
  },

  /**
   * Calculate splice loss using linear regression
   */
  async getSpliceLoss(sessionId: string): Promise<SpliceLossResult> {
    const response = await analysisApi.post<SpliceLossResult>(
      `/api/fiber/splice-loss/${encodeURIComponent(sessionId)}`
    )
    return response.data
  },

  /**
   * Detect events/imperfections in the trace
   */
  async detectEvents(sessionId: string): Promise<EventDetectionResult> {
    const response = await analysisApi.post<EventDetectionResult>(
      `/api/fiber/events/${encodeURIComponent(sessionId)}`
    )
    return response.data
  },
}

// ============================================================================
// Admin API (SuperAdmin only)
// ============================================================================

export const adminApi = {
  async listCustomers(page = 0, size = 20): Promise<CustomerListResponse> {
    const response = await api.get<CustomerListResponse>('/api/v1/admin/customers', {
      params: { page, size }
    })
    return response.data
  },

  async suspendCustomer(customerId: string): Promise<SuspendActivateResponse> {
    const response = await api.post<SuspendActivateResponse>(
      `/api/v1/admin/customers/${customerId}/suspend`
    )
    return response.data
  },

  async activateCustomer(customerId: string): Promise<SuspendActivateResponse> {
    const response = await api.post<SuspendActivateResponse>(
      `/api/v1/admin/customers/${customerId}/activate`
    )
    return response.data
  },

  async getCaStatus(): Promise<CaStatusResponse> {
    const response = await api.get<CaStatusResponse>('/api/v1/admin/certificates/ca-status')
    return response.data
  },

  async getPendingDomains(): Promise<PendingDomain[]> {
    const response = await api.get<PendingDomain[]>('/api/v1/admin/domains/pending')
    return response.data
  },

  async approveDomain(userId: string): Promise<void> {
    await api.post(`/api/v1/admin/domains/${userId}/approve`)
  },

  async rejectDomain(userId: string): Promise<void> {
    await api.post(`/api/v1/admin/domains/${userId}/reject`)
  },

  async resetDomain(userId: string): Promise<void> {
    await api.post(`/api/v1/admin/domains/${userId}/reset`)
  },
}

// ============================================================================
// Certificate API (SuperAdmin only)
// ============================================================================

export const certificateApi = {
  async listDeviceCertificates(
    page = 0, size = 20, customerId?: string, issuerCn?: string
  ): Promise<DeviceCertificateListResponse> {
    const response = await api.get<DeviceCertificateListResponse>('/api/v1/devices/certificates', {
      params: { page, size, ...(customerId && { customerId }), ...(issuerCn && { issuerCn }) }
    })
    return response.data
  },

  async requestRotation(deviceId: string): Promise<void> {
    await api.post(`/api/v1/devices/${deviceId}/certificate/rotate`)
  },

  async requestBulkRotation(expiryDays: number): Promise<void> {
    await api.post('/api/v1/devices/certificates/rotate-bulk', { expiryDays })
  }
}

// Export the axios instances for direct use if needed
export { api, fileApi, analysisApi }
